import { useState, useEffect } from "react";
import { Database, Wifi, WifiOff, CheckCircle, AlertCircle, RefreshCw, Users, FileText, Clock } from "lucide-react";
import { db } from "config/firebase.config";
import { collection, getDocs, doc, getDoc, onSnapshot, setDoc, deleteDoc } from "firebase/firestore";
import toast from "react-hot-toast";

const FirebaseStatus = () => {
    const [connectionStatus, setConnectionStatus] = useState('checking');
    const [dataStats, setDataStats] = useState({
        users: 0,
        leaveRequests: 0,
        timesheetApprovals: 0,
        lastSync: null
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        checkFirebaseConnection();
        setupRealtimeListeners();
    }, []);

    const checkFirebaseConnection = async () => {
        setIsLoading(true);
        setConnectionStatus('checking');
        setError(null);

        try {
            // Test basic connection by trying to read from Firestore
            const usersRef = collection(db, "users");
            const snapshot = await getDocs(usersRef);
            
            setConnectionStatus('connected');
            setDataStats(prev => ({
                ...prev,
                users: snapshot.size,
                lastSync: new Date().toLocaleString()
            }));

            console.log('✅ Firebase connection successful');
            console.log(`📊 Found ${snapshot.size} users in Firestore`);
            
        } catch (error) {
            console.error('❌ Firebase connection failed:', error);
            setConnectionStatus('disconnected');
            setError(error.message);
            toast.error(`Firebase connection failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const setupRealtimeListeners = () => {
        try {
            // Listen to users collection
            const usersUnsub = onSnapshot(collection(db, "users"), (snapshot) => {
                setDataStats(prev => ({
                    ...prev,
                    users: snapshot.size,
                    lastSync: new Date().toLocaleString()
                }));
            });

            // Listen to leave requests collection
            const leaveUnsub = onSnapshot(collection(db, "leaveRequests"), (snapshot) => {
                setDataStats(prev => ({
                    ...prev,
                    leaveRequests: snapshot.size
                }));
            });

            // Listen to timesheet approvals collection
            const timesheetUnsub = onSnapshot(collection(db, "timesheetApprovals"), (snapshot) => {
                setDataStats(prev => ({
                    ...prev,
                    timesheetApprovals: snapshot.size
                }));
            });

            // Cleanup function
            return () => {
                usersUnsub();
                leaveUnsub();
                timesheetUnsub();
            };
        } catch (error) {
            console.error('Error setting up realtime listeners:', error);
        }
    };

    const testFirebaseOperations = async () => {
        setIsLoading(true);
        try {
            // Test read operation
            const usersRef = collection(db, "users");
            const usersSnapshot = await getDocs(usersRef);
            
            // Test write operation (create a test document)
            const testRef = doc(collection(db, "test"));
            await setDoc(testRef, {
                test: true,
                timestamp: new Date().toISOString()
            });
            
            // Clean up test document
            await deleteDoc(testRef);
            
            toast.success('Firebase read/write operations successful!');
            console.log('✅ Firebase operations test passed');
            
        } catch (error) {
            console.error('❌ Firebase operations test failed:', error);
            toast.error(`Firebase operations failed: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const getConnectionIcon = () => {
        switch (connectionStatus) {
            case 'connected':
                return <Wifi className="text-green-600" size={20} />;
            case 'disconnected':
                return <WifiOff className="text-red-600" size={20} />;
            default:
                return <RefreshCw className="animate-spin text-blue-600" size={20} />;
        }
    };

    const getConnectionColor = () => {
        switch (connectionStatus) {
            case 'connected':
                return 'border-green-200 bg-green-50';
            case 'disconnected':
                return 'border-red-200 bg-red-50';
            default:
                return 'border-blue-200 bg-blue-50';
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" style={{
            display: 'none'
        }}>
            <div className="flex items-center gap-2 mb-4">
                <Database className="text-blue-600" size={24} />
                <h3 className="text-lg font-semibold text-gray-800">Firebase Connection Status</h3>
            </div>

            {/* Connection Status */}
            <div className={`border rounded-lg p-4 mb-4 ${getConnectionColor()}`}>
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        {getConnectionIcon()}
                        <span className="font-medium">
                            {connectionStatus === 'connected' && 'Connected to Firebase'}
                            {connectionStatus === 'disconnected' && 'Disconnected from Firebase'}
                            {connectionStatus === 'checking' && 'Checking connection...'}
                        </span>
                    </div>
                    <button
                        onClick={checkFirebaseConnection}
                        disabled={isLoading}
                        className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded hover:bg-blue-200 disabled:opacity-50"
                    >
                        <RefreshCw size={14} className={isLoading ? 'animate-spin' : ''} />
                        Refresh
                    </button>
                </div>

                {error && (
                    <div className="mt-2 p-2 bg-red-100 border border-red-200 rounded text-sm text-red-700">
                        <strong>Error:</strong> {error}
                    </div>
                )}
            </div>

            {/* Data Statistics */}
            {connectionStatus === 'connected' && (
                <div className="mb-4">
                    <h4 className="font-medium text-gray-800 mb-3">Data Statistics (Real-time)</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <Users className="text-blue-600" size={16} />
                                <span className="font-medium text-blue-800">Users</span>
                            </div>
                            <p className="text-2xl font-bold text-blue-900">{dataStats.users}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <FileText className="text-green-600" size={16} />
                                <span className="font-medium text-green-800">Leave Requests</span>
                            </div>
                            <p className="text-2xl font-bold text-green-900">{dataStats.leaveRequests}</p>
                        </div>
                        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                            <div className="flex items-center gap-2 mb-1">
                                <Clock className="text-purple-600" size={16} />
                                <span className="font-medium text-purple-800">Timesheet Approvals</span>
                            </div>
                            <p className="text-2xl font-bold text-purple-900">{dataStats.timesheetApprovals}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Connection Details */}
            <div className="mb-4">
                <h4 className="font-medium text-gray-800 mb-2">Connection Details</h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-sm">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <p><strong>Project ID:</strong> billbergia-timesheets</p>
                        <p><strong>Database:</strong> Firestore</p>
                        <p><strong>Region:</strong> us-central1</p>
                        <p><strong>Last Sync:</strong> {dataStats.lastSync || 'Never'}</p>
                    </div>
                </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2">
                <button
                    onClick={testFirebaseOperations}
                    disabled={isLoading || connectionStatus !== 'connected'}
                    className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isLoading ? (
                        <RefreshCw className="animate-spin" size={16} />
                    ) : (
                        <CheckCircle size={16} />
                    )}
                    Test Operations
                </button>
                
                <button
                    onClick={() => window.open('https://console.firebase.google.com/project/billbergia-timesheets', '_blank')}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                    <Database size={16} />
                    Firebase Console
                </button>
            </div>

            {/* Data Sync Status */}
            {connectionStatus === 'connected' && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                        <CheckCircle className="text-green-600" size={16} />
                        <span className="text-sm text-green-700">
                            <strong>Data is synced in real-time</strong> - All changes are automatically synchronized with Firebase
                        </span>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FirebaseStatus;
